// src/components/TrackLoop.tsx
import {
  getTrackReferenceId
} from "@livekit/components-core";

// src/context/track-reference-context.ts
import { createContext, useContext } from "solid-js";
var TrackRefContext = createContext(void 0);
function useTrackRefContext() {
  const trackReference = useContext(TrackRefContext);
  if (!trackReference) {
    throw Error("tried to access track context outside of track context provider");
  }
  return trackReference;
}
function useMaybeTrackRefContext() {
  return useContext(TrackRefContext);
}
function useEnsureTrackRef(trackRef) {
  const context = useMaybeTrackRefContext();
  const ref = trackRef ?? context;
  if (!ref) {
    throw new Error(
      "No TrackRef, make sure you are inside a TrackRefContext or pass the TrackRef explicitly"
    );
  }
  return ref;
}

// src/components/TrackLoop.tsx
import { Key } from "@solid-primitives/keyed";
function TrackLoop({ tracks, ...props }) {
  return <Key each={tracks()} by={(item) => getTrackReferenceId(item)}>{(trackReference) => <TrackRefContext.Provider value={trackReference()}>{props.children({})}</TrackRefContext.Provider>}</Key>;
}

// src/signals/useMediaTrackBySourceOrName.ts
import { isTrackReference } from "@livekit/components-core";
import { setupMediaTrack, log, isLocal, getTrackByIdentifier } from "@livekit/components-core";
import { createEffect, createMemo, createSignal } from "solid-js";
function useMediaTrackBySourceOrName(observerOptions, options = {}) {
  const [publication, setPublication] = createSignal(getTrackByIdentifier(observerOptions));
  const [isMuted, setMuted] = createSignal(publication()?.isMuted);
  const [isSubscribed, setSubscribed] = createSignal(publication()?.isSubscribed);
  const [track, setTrack] = createSignal(publication()?.track);
  const [orientation, setOrientation] = createSignal("landscape");
  let previousElement;
  const mediaTrack = createMemo(() => {
    return setupMediaTrack(observerOptions);
  }, [
    observerOptions.participant.sid ?? observerOptions.participant.identity,
    observerOptions.source,
    isTrackReference(observerOptions) && observerOptions.publication.trackSid
  ]);
  createEffect(() => {
    const subscription = mediaTrack().trackObserver.subscribe((publication2) => {
      log.debug("update track", publication2);
      setPublication(publication2);
      setMuted(publication2?.isMuted);
      setSubscribed(publication2?.isSubscribed);
      setTrack(publication2?.track);
    });
    return () => subscription.unsubscribe();
  });
  createEffect(() => {
    const t = track();
    const el = options.element?.();
    if (t) {
      if (previousElement) {
        t.detach(previousElement);
      }
      if (el && !(isLocal(observerOptions.participant) && t.kind === "audio")) {
        t.attach(el);
      }
    }
    previousElement = el;
    return () => {
      if (previousElement) {
        t?.detach(previousElement);
      }
    };
  });
  createEffect(() => {
    const pub = publication();
    if (typeof pub?.dimensions?.width === "number" && typeof pub.dimensions?.height === "number") {
      const orientation_ = pub.dimensions.width > pub.dimensions.height ? "landscape" : "portrait";
      setOrientation(orientation_);
    }
  });
  return {
    publication,
    isMuted,
    isSubscribed,
    track,
    elementProps: () => ({
      ...options.props,
      ...{
        className: mediaTrack().className,
        "data-lk-local-participant": observerOptions.participant.isLocal,
        "data-lk-source": publication()?.source,
        ...publication()?.kind === "video" && { "data-lk-orientation": orientation }
      }
    })
  };
}

// src/components/participant/AudioTrack.tsx
import { log as log3 } from "@livekit/components-core";
import { RemoteAudioTrack, RemoteTrackPublication } from "livekit-client";

// src/context/participant-context.ts
import { createContext as createContext2, useContext as useContext2 } from "solid-js";
var ParticipantContext = createContext2(void 0);
function useParticipantContext() {
  const participant = useContext2(ParticipantContext);
  if (!participant) {
    throw Error("tried to access participant context outside of participant context provider");
  }
  return participant;
}
function useMaybeParticipantContext() {
  return useContext2(ParticipantContext);
}
function useEnsureParticipant(participant) {
  const context = useMaybeParticipantContext();
  const trackContext = useMaybeTrackRefContext();
  const p = participant ?? context ?? trackContext?.participant;
  if (!p) {
    throw new Error(
      "No participant provided, make sure you are inside a participant context or pass the participant explicitly"
    );
  }
  return p;
}

// src/context/room-context.ts
import { createContext as createContext3, useContext as useContext3 } from "solid-js";
var RoomContext = createContext3(void 0);
function useRoomContext() {
  const ctx = useContext3(RoomContext);
  if (!ctx) {
    throw Error("tried to access room context outside of livekit room component");
  }
  return ctx;
}
function useMaybeRoomContext() {
  return useContext3(RoomContext);
}
function useEnsureRoom(room) {
  const context = useMaybeRoomContext();
  return () => {
    const r = room ?? context?.();
    if (!r) {
      throw new Error(
        "No room provided, make sure you are inside a Room context or pass the room explicitly"
      );
    }
    return r;
  };
}

// src/components/participant/AudioTrack.tsx
import { createEffect as createEffect6, onCleanup as onCleanup2 } from "solid-js";

// src/signals/useConnectionStatus.ts
import { connectionStateObserver } from "@livekit/components-core";

// src/signals/internal/useObservableState.ts
import { createEffect as createEffect2, createSignal as createSignal2 } from "solid-js";
function useObservableState(observable, startWith) {
  const [state, setState] = createSignal2(startWith);
  createEffect2(() => {
    if (typeof window === "undefined" || !observable)
      return;
    const subscription = observable.subscribe(setState);
    return () => subscription.unsubscribe();
  }, [observable]);
  return state;
}

// src/signals/useConnectionStatus.ts
import { createMemo as createMemo2 } from "solid-js";
function useConnectionState(room) {
  const r = useEnsureRoom(room);
  const observable = createMemo2(() => connectionStateObserver(r()));
  const connectionState = useObservableState(
    observable(),
    r().state
  );
  return connectionState;
}

// src/signals/useIsEncrypted.ts
import { encryptionStatusObservable } from "@livekit/components-core";
import { createMemo as createMemo3 } from "solid-js";
import { LocalParticipant } from "livekit-client";
function useIsEncrypted(participant) {
  const p = useEnsureParticipant(participant);
  const room = useEnsureRoom();
  const observer = createMemo3(() => encryptionStatusObservable(room(), p));
  const isEncrypted = useObservableState(
    observer(),
    p instanceof LocalParticipant ? p.isE2EEEnabled : p.isEncrypted
  );
  return isEncrypted;
}

// src/signals/useIsMuted.ts
import {
  getTrackReferenceId as getTrackReferenceId2,
  mutedObserver
} from "@livekit/components-core";
import { createEffect as createEffect3, createSignal as createSignal3 } from "solid-js";
function useIsMuted(sourceOrTrackRef, options = {}) {
  const passedParticipant = typeof sourceOrTrackRef === "string" ? options.participant : sourceOrTrackRef.participant;
  const p = useEnsureParticipant(passedParticipant);
  const ref = typeof sourceOrTrackRef === "string" ? { participant: p, source: sourceOrTrackRef } : sourceOrTrackRef;
  const [isMuted, setIsMuted] = createSignal3(
    !!(ref.publication?.isMuted || p.getTrackPublication(ref.source)?.isMuted)
  );
  createEffect3(() => {
    const listener = mutedObserver(ref).subscribe(setIsMuted);
    return () => listener.unsubscribe();
  }, [getTrackReferenceId2(ref)]);
  return isMuted;
}

// src/signals/useIsSpeaking.ts
import { createIsSpeakingObserver } from "@livekit/components-core";
import { createMemo as createMemo4 } from "solid-js";
function useIsSpeaking(participant) {
  const p = useEnsureParticipant(participant);
  const observable = createMemo4(() => createIsSpeakingObserver(p), [p]);
  const isSpeaking = useObservableState(
    observable(),
    p.isSpeaking
  );
  return isSpeaking;
}

// src/signals/useMediaDevices.ts
import { createMediaDeviceObserver } from "@livekit/components-core";
import { createMemo as createMemo5 } from "solid-js";
function useMediaDevices({
  kind,
  onError
}) {
  const deviceObserver = createMemo5(() => createMediaDeviceObserver(kind, onError));
  const devices = useObservableState(deviceObserver(), []);
  return devices;
}

// src/signals/useMediaDeviceSelect.ts
import { createMediaDeviceObserver as createMediaDeviceObserver2, setupDeviceSelector } from "@livekit/components-core";
import { Room } from "livekit-client";
import { createEffect as createEffect4, createMemo as createMemo6, createSignal as createSignal4, on, onCleanup } from "solid-js";
function useMediaDeviceSelect({
  kind,
  room,
  track,
  requestPermissions,
  onError
}) {
  const roomContext = useMaybeRoomContext();
  const roomFallback = createMemo6(() => room ?? roomContext?.() ?? new Room());
  const deviceObserver = createMemo6(
    () => createMediaDeviceObserver2(kind, onError, requestPermissions)
  );
  const devices = useObservableState(deviceObserver(), []);
  const [currentDeviceId, setCurrentDeviceId] = createSignal4(
    roomFallback()?.getActiveDevice(kind) ?? "default"
  );
  const deviceSelector = createMemo6(() => setupDeviceSelector(kind, roomFallback()));
  createEffect4(
    on(
      () => deviceSelector(),
      ({ activeDeviceObservable }) => {
        const listener = activeDeviceObservable.subscribe((deviceId) => {
          if (!deviceId) {
            return;
          }
          setCurrentDeviceId(deviceId);
        });
        onCleanup(() => {
          listener?.unsubscribe();
        });
      }
    )
  );
  return {
    devices,
    className: () => deviceSelector().className,
    activeDeviceId: currentDeviceId,
    setActiveMediaDevice: (...args) => deviceSelector().setActiveMediaDevice(...args)
  };
}

// src/signals/useTracks.ts
import { createEffect as createEffect5, createMemo as createMemo7, createSignal as createSignal5 } from "solid-js";
import {
  isSourceWitOptions,
  isSourcesWithOptions,
  log as log2,
  trackReferencesObservable
} from "@livekit/components-core";
import { Track } from "livekit-client";
function useTracks(sources = [
  Track.Source.Camera,
  Track.Source.Microphone,
  Track.Source.ScreenShare,
  Track.Source.ScreenShareAudio,
  Track.Source.Unknown
], options = {}) {
  const roomCtx = useMaybeRoomContext();
  const room = () => options.room ?? roomCtx?.();
  const [trackReferences, setTrackReferences] = createSignal5([]);
  const [participants, setParticipants] = createSignal5([]);
  const sources_ = createMemo7(() => {
    return sources.map((s) => isSourceWitOptions(s) ? s.source : s);
  });
  createEffect5(() => {
    const r = room();
    if (!r) {
      setTrackReferences([]);
      setParticipants([]);
      return void 0;
    }
    const subscription = trackReferencesObservable(r, sources_(), {
      additionalRoomEvents: options.updateOnlyOn,
      onlySubscribed: options.onlySubscribed
    }).subscribe(({ trackReferences: trackReferences2, participants: participants2 }) => {
      log2.debug("setting track bundles", trackReferences2, participants2);
      setTrackReferences(trackReferences2);
      setParticipants(participants2);
    });
    return () => subscription.unsubscribe();
  }, [room(), JSON.stringify(options.updateOnlyOn), JSON.stringify(sources)]);
  const maybeTrackReferences = createMemo7(() => {
    if (isSourcesWithOptions(sources)) {
      const requirePlaceholder = requiredPlaceholders(sources, participants());
      const trackReferencesWithPlaceholders = Array.from(
        trackReferences()
      );
      participants().forEach((participant) => {
        if (requirePlaceholder.has(participant.identity)) {
          const sourcesToAddPlaceholder = requirePlaceholder.get(participant.identity) ?? [];
          sourcesToAddPlaceholder.forEach((placeholderSource) => {
            if (trackReferences().find(
              ({ participant: p, publication }) => participant.identity === p.identity && publication.source === placeholderSource
            )) {
              return;
            }
            log2.debug(
              `Add ${placeholderSource} placeholder for participant ${participant.identity}.`
            );
            const placeholder = {
              participant,
              source: placeholderSource
            };
            trackReferencesWithPlaceholders.push(placeholder);
          });
        }
      });
      return trackReferencesWithPlaceholders;
    } else {
      return trackReferences();
    }
  });
  return maybeTrackReferences;
}
function difference(setA, setB) {
  const _difference = new Set(setA);
  for (const elem of setB) {
    _difference.delete(elem);
  }
  return _difference;
}
function requiredPlaceholders(sources, participants) {
  const placeholderMap = /* @__PURE__ */ new Map();
  if (isSourcesWithOptions(sources)) {
    const sourcesThatNeedPlaceholder = sources.filter((sourceWithOption) => sourceWithOption.withPlaceholder).map((sourceWithOption) => sourceWithOption.source);
    participants.forEach((participant) => {
      const sourcesOfSubscribedTracks = participant.getTrackPublications().map((pub) => pub.track?.source).filter((trackSource) => trackSource !== void 0);
      const placeholderNeededForThisParticipant = Array.from(
        difference(new Set(sourcesThatNeedPlaceholder), new Set(sourcesOfSubscribedTracks))
      );
      if (placeholderNeededForThisParticipant.length > 0) {
        placeholderMap.set(participant.identity, placeholderNeededForThisParticipant);
      }
    });
  }
  return placeholderMap;
}

// src/components/participant/AudioTrack.tsx
function AudioTrack(props) {
  const trackReference = useEnsureTrackRef(props.trackRef);
  let mediaEl;
  const { elementProps, isSubscribed, track, publication } = useMediaTrackBySourceOrName(
    trackReference,
    {
      element: () => mediaEl,
      props
    }
  );
  createEffect6(() => {
    props.onSubscriptionStatusChanged?.(!!isSubscribed());
  });
  let gainContext;
  const { activeDeviceId } = useMediaDeviceSelect({ kind: "audiooutput" });
  onCleanup2(() => {
    if (gainContext) {
      gainContext.gainNode.disconnect();
      gainContext.audioContext.close();
    }
  });
  createEffect6(() => {
    const t = track();
    if (t === void 0 || props.volume === void 0) {
      return;
    }
    if (t instanceof RemoteAudioTrack) {
      if (!props.enableBoosting || props.volume <= 1) {
        t.setVolume(props.volume);
        if (gainContext) {
          gainContext.gainNode.disconnect();
          gainContext.audioContext.close();
          gainContext = void 0;
        }
      } else {
        if (gainContext) {
          gainContext.gainNode.gain.value = props.volume;
        } else {
          t.setVolume(0);
          const audioContext = new AudioContext();
          if ("setSinkId" in AudioContext.prototype) {
            ;
            audioContext.setSinkId(
              activeDeviceId()
            );
          } else {
          }
          const gainNode = audioContext.createGain();
          const source = audioContext.createMediaStreamSource(new MediaStream([t.mediaStreamTrack]));
          gainNode.gain.value = props.volume;
          gainNode.connect(audioContext.destination);
          source.connect(gainNode);
          gainContext = {
            audioContext,
            gainNode
          };
        }
      }
    } else {
      log3.warn("Volume can only be set on remote audio tracks.");
    }
  });
  createEffect6(() => {
    const pub = publication();
    if (pub === void 0 || props.muted === void 0) {
      return;
    }
    if (pub instanceof RemoteTrackPublication) {
      pub.setEnabled(!props.muted);
    } else {
      log3.warn("Can only call setEnabled on remote track publications.");
    }
  });
  return <audio ref={mediaEl} {...elementProps()} />;
}

// src/components/participant/VideoTrack.tsx
import { RemoteTrackPublication as RemoteTrackPublication2 } from "livekit-client";
import { createEffect as createEffect7, createSignal as createSignal6 } from "solid-js";
import { createVisibilityObserver } from "@solid-primitives/intersection-observer";
import { debounce } from "@solid-primitives/scheduled";
function VideoTrack({
  onTrackClick,
  onClick,
  onSubscriptionStatusChanged,
  trackRef,
  manageSubscription,
  ...props
}) {
  const trackReference = useEnsureTrackRef(trackRef);
  let mediaEl;
  const useVisibilityObserver = createVisibilityObserver({ threshold: 0.8 });
  const visible = useVisibilityObserver(() => mediaEl);
  const [maintainedVisibility, setMaintainedVisibility] = createSignal6();
  const updateVisibility = debounce((vis) => setMaintainedVisibility(vis), 3e3);
  createEffect7(() => updateVisibility(visible()));
  createEffect7(() => {
    if (manageSubscription && trackReference.publication instanceof RemoteTrackPublication2 && maintainedVisibility() === false && visible() === false) {
      trackReference.publication.setSubscribed(false);
    }
  });
  createEffect7(() => {
    if (manageSubscription && trackReference.publication instanceof RemoteTrackPublication2 && visible()) {
      trackReference.publication.setSubscribed(true);
    }
  });
  const { elementProps, publication, isSubscribed } = useMediaTrackBySourceOrName(trackReference, {
    element: () => mediaEl,
    // @ts-expect-error ???
    props
  });
  createEffect7(() => {
    onSubscriptionStatusChanged?.(!!isSubscribed());
  });
  const clickHandler = (evt) => {
    ;
    onClick?.(evt);
    onTrackClick?.({ participant: trackReference?.participant, track: publication() });
  };
  return <video ref={mediaEl} {...elementProps} muted={true} onClick={clickHandler} />;
}

// src/index.tsx
import { setLogLevel, setLogExtension, isTrackReference as isTrackReference2 } from "@livekit/components-core";
export {
  AudioTrack,
  ParticipantContext,
  RoomContext,
  TrackLoop,
  TrackRefContext,
  VideoTrack,
  isTrackReference2 as isTrackReference,
  setLogExtension,
  setLogLevel,
  useConnectionState,
  useEnsureParticipant,
  useEnsureRoom,
  useEnsureTrackRef,
  useIsEncrypted,
  useIsMuted,
  useIsSpeaking,
  useMaybeParticipantContext,
  useMaybeRoomContext,
  useMaybeTrackRefContext,
  useMediaDeviceSelect,
  useMediaDevices,
  useMediaTrackBySourceOrName,
  useParticipantContext,
  useRoomContext,
  useTrackRefContext,
  useTracks
};
